import type { LucideIcon } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

/**
 * An action button for the delivery-tasks page.
 *
 * Not the shared `Button variant="outline"`: that variant sets `bg-background`
 * but no text colour, so it inherits whatever the surrounding surface hands it
 * and is only legible once `hover:text-accent-foreground` kicks in.
 *
 * The two surfaces this page uses need opposite treatments — `.hero-panel` is
 * dark navy while `.glow-card-static` is `--card` (white) — so `surface` picks
 * the label colour explicitly. Either way it is bold and full-contrast at rest,
 * with hover only shifting the border and fill.
 */
export function ActionButton({
  label,
  icon: Icon,
  onClick,
  surface,
  className,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  /** "hero" = the dark hero panel; "card" = a light section card. */
  surface: "hero" | "card";
  className?: string;
}) {
  const onHero = surface === "hero";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg border font-bold transition-all focus-visible:outline-none",
        onHero
          ? [
              "border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] text-[oklch(0.93_0.008_240)]",
              "hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] hover:text-[oklch(0.97_0.005_240)]",
              "focus-visible:border-[oklch(0.72_0.17_190_/_0.6)]",
            ]
          : [
              // Mapped theme tokens only: --primary-soft/--primary-strong exist as
              // raw vars but aren't in the @theme colour map, so `bg-primary-soft`
              // would compile to nothing.
              "border-border text-foreground bg-transparent px-3 py-1.5 text-[11px]",
              "hover:border-primary/50 hover:bg-muted hover:text-primary",
              "focus-visible:border-primary/60",
            ],
        className
      )}
    >
      <Icon className={onHero ? "size-4" : "size-3.5"} aria-hidden />
      {label}
    </button>
  );
}
